import { motion } from 'framer-motion'
import Card from '@/components/ui/Card'
import AnimatedNumber from '@/components/common/AnimatedNumber'
import { cn } from '@/lib/utils'
import type { CostSimulationResponse } from '@/types/cost'

interface CostBreakdownProps {
  /** 시뮬레이션 결과 */
  result: CostSimulationResponse
  /** 비급여 포함 여부 */
  includeNonCovered: boolean
  /** 추가 CSS 클래스 */
  className?: string
}

/** 금액을 만원 범위로 포맷 */
function formatManWonRange(min: number, max: number): string {
  const minMan = Math.round(min / 10000)
  const maxMan = Math.round(max / 10000)
  if (minMan === maxMan) {
    return `약 ${minMan.toLocaleString()}만원`
  }
  return `${minMan.toLocaleString()}~${maxMan.toLocaleString()}만원`
}

/**
 * 비용 상세 분해 카드 v2.0
 *
 * - 급여 본인부담금 (정확한 금액)
 * - 비급여 추가비용 (범위)
 * - 합계 (범위)
 * - 시각적 바 차트로 비율 표시
 */
export default function CostBreakdown({
  result,
  includeNonCovered,
  className,
}: CostBreakdownProps) {
  const { copayAmount, nonCoveredEstimate, totalEstimate } = result

  // 비율 계산 (시각적 바용)
  const maxAmount = totalEstimate.max || 1
  const copayPercent = Math.round((copayAmount / maxAmount) * 100)
  // nonCoveredEstimate 는 백엔드에서 null 가능 → 표시 시 0 으로 폴백
  const nonCoveredPercent = includeNonCovered && nonCoveredEstimate
    ? Math.round((nonCoveredEstimate.max / maxAmount) * 100)
    : 0

  return (
    <Card className={cn('', className)}>
      <h3 className="text-body font-bold text-cb-gray-900 mb-5">
        비용 상세
      </h3>

      <div className="flex flex-col gap-4">
        {/* 급여 본인부담금 */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-body-sm text-cb-gray-600">급여 본인부담금</span>
            <span className="text-body-sm font-bold text-cb-gray-900 text-amount">
              <AnimatedNumber
                value={copayAmount}
                duration={800}
                format={(v) => `${Math.round(v).toLocaleString()}원`}
              />
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-cb-gray-100">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-cb-primary to-blue-400"
              initial={{ width: 0 }}
              animate={{ width: `${copayPercent}%` }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay: 0.2 }}
            />
          </div>
        </div>

        {/* 비급여 추가비용 — nonCoveredEstimate 가 null 인 경우는 표시 자체 생략 */}
        {includeNonCovered && nonCoveredEstimate && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-body-sm text-cb-gray-600">비급여 추가비용</span>
              <span className="text-body-sm font-bold text-cb-gray-900 text-amount">
                {formatManWonRange(nonCoveredEstimate.min, nonCoveredEstimate.max)}
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-cb-gray-100">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400"
                initial={{ width: 0 }}
                animate={{ width: `${nonCoveredPercent}%` }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay: 0.4 }}
              />
            </div>
          </div>
        )}

        {/* 한도 초과 자부담 안내 */}
        {result.limitExcessAmount > 0 && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-body-sm font-semibold text-amber-700">한도 초과 자부담</span>
              <span className="text-body-sm font-bold text-amber-700 text-amount">
                {Math.round(result.limitExcessAmount).toLocaleString()}원
              </span>
            </div>
            <p className="text-caption text-amber-600">
              월 한도액({(result.monthlyLimit ?? 0).toLocaleString()}원)을 초과하여 {Math.round(result.limitExcessAmount).toLocaleString()}원은 전액 본인부담입니다
            </p>
          </div>
        )}

        {/* 구분선 */}
        <hr className="border-cb-gray-200" />

        {/* 합계 */}
        <div className="flex items-center justify-between rounded-2xl bg-cb-primary-light px-4 py-3">
          <span className="text-body font-bold text-cb-gray-900">합계</span>
          <span className="text-h3 font-bold text-cb-primary text-amount">
            {formatManWonRange(totalEstimate.min, totalEstimate.max)}
          </span>
        </div>
      </div>
    </Card>
  )
}
